import React, { useCallback, useEffect, useState } from 'react';
import { AttributionControl, MapContainer, ZoomControl, useMapEvent, Pane, useMap } from 'react-leaflet';
import { useLocation } from 'react-router';
import 'leaflet/dist/leaflet.css';
import './map.css';
import { apiGet } from '../apiHelpers';
import { initialMapViewport, mapBackgroundColor } from '../config/map-config';
import { CityBaseMapLayer } from './layers/city-base-map-layer';
// import { CityBoundaryLayer } from './layers/city-boundary-layer';
import { BoroughBoundaryLayer } from './layers/borough-boundary-layer';
import { BoroughLabelLayer } from './layers/borough-label-layer';
import { ParcelBoundaryLayer } from './layers/parcel-boundary-layer';
import { HistoricDataLayer } from './layers/historic-data-layer';
import { HistoricMapLayer } from './layers/historic-map-layer';
import { FloodBoundaryLayer } from './layers/flood-boundary-layer';
import { ConservationAreaBoundaryLayer } from './layers/conservation-boundary-layer';
import { VistaBoundaryLayer } from './layers/vista-boundary-layer';
import { GovernorateBoundaryLayer } from './layers/governorate-boundary-layer';
import { AwardsBordersLayer } from './layers/awards-borders-layer';
import { ArchaeologicalSitesLayer } from './layers/archaeological-sites-layer';
import { NationalHeritageLayer } from './layers/national-heritage-layer';
import { WorldHeritagePropertiesLayer } from './layers/world-heritage-properties-layer';
import { UrbanHeritageLayer } from './layers/urban-heritage-layer';
import { BuildingBaseLayer } from './layers/building-base-layer';
import { BuildingDataLayer } from './layers/building-data-layer';
import { BuildingNumbersLayer } from './layers/building-numbers-layer';
import { BuildingHighlightLayer } from './layers/building-highlight-layer';
import { Legend } from './legend';
import { Logo } from '../components/logo';
import SearchBox from './search-box';
import ThemeSwitcher from './theme-switcher';
import DataLayerSwitcher from './data-switcher';
import { SimpleLayerButton } from './simple-layer-button';
import { useDisplayPreferences } from '../displayPreferences-context';
import { doesMapStyleColourBuildings } from '../config/category-maps-config';
import './legend.css';
export const ColouringMap = ({ mode, revisionId, onBuildingAction, selectedBuildingId, mapColourScale, onMapColourScale, categoryMapDefinitions, children }) => {
    const { darkLightTheme, darkLightThemeSwitch, showLayerSelection, governoratesSwitchOnClick, parcelSwitchOnClick, conservationSwitchOnClick, editableBuildingsSwitchOnClick, parcel, conservation, editableBuildings, governorates, vista, flood, borough, historicData, historicMap, boroughSwitchOnClick } = useDisplayPreferences();
    const [position, setPosition] = useState(initialMapViewport.position);
    const [zoom, setZoom] = useState(initialMapViewport.zoom);
    const [comingSoonTitle, setComingSoonTitle] = useState(null);
    const location = useLocation();
    const toggleComingSoon = useCallback((title) => {
        setComingSoonTitle((current) => (current === title ? null : title));
    }, []);
    // Clear layer Coming soon when navigating sidebar categories (e.g. Green / Water)
    useEffect(() => {
        setComingSoonTitle(null);
    }, [location.pathname]);
    const handleLocate = useCallback((lat, lng, zoom) => {
        setPosition([lat, lng]);
        setZoom(zoom);
    }, []);
    const handleClick = useCallback(async (e) => {
        // No building pick when Editable Buildings is off (Coming soon categories)
        if (editableBuildings !== 'enabled') {
            return;
        }
        const { lat, lng } = e.latlng;
        const data = await apiGet(`/api/buildings/locate?lat=${lat}&lng=${lng}`);
        const building = data === null || data === void 0 ? void 0 : data[0];
        onBuildingAction(building);
    }, [onBuildingAction, editableBuildings]);
    return (React.createElement("div", { className: "map-container" },
        React.createElement(MapContainer, { center: initialMapViewport.position, zoom: initialMapViewport.zoom, minZoom: 9, maxZoom: 18, doubleClickZoom: false, zoomControl: false, attributionControl: false },
            React.createElement(ClickHandler, { onClick: handleClick }),
            React.createElement(MapBackgroundColor, { theme: darkLightTheme }),
            React.createElement(MapViewport, { position: position, zoom: zoom }),
            React.createElement(Pane, { key: darkLightTheme, name: 'cc-base-pane', style: { zIndex: 50 } },
                React.createElement(CityBaseMapLayer, { theme: darkLightTheme }),
                React.createElement(BuildingBaseLayer, { theme: darkLightTheme })),
            mapColourScale &&
                doesMapStyleColourBuildings(mapColourScale, categoryMapDefinitions) &&
                React.createElement(BuildingDataLayer, { tileset: mapColourScale, revisionId: revisionId }),
            React.createElement(Pane, { name: 'cc-overlay-pane', style: { zIndex: 300 } },
                historicMap === 'enabled' && React.createElement(HistoricMapLayer, { revisionId: revisionId }),
                historicData === 'enabled' && React.createElement(HistoricDataLayer, { revisionId: revisionId }),
                borough === 'enabled' && React.createElement(BoroughBoundaryLayer, null),
                parcel === 'enabled' && React.createElement(ParcelBoundaryLayer, null),
                flood === 'enabled' && React.createElement(FloodBoundaryLayer, null),
                vista === 'enabled' && React.createElement(VistaBoundaryLayer, null),
                governorates === 'enabled' && mapColourScale !== 'designer_awards' && React.createElement(GovernorateBoundaryLayer, null),
                mapColourScale === 'designer_awards' && React.createElement(AwardsBordersLayer, null),
                conservation === 'enabled' && React.createElement(ConservationAreaBoundaryLayer, null),
                mapColourScale === 'historic_area_classifications' && (React.createElement(React.Fragment, null,
                    React.createElement(UrbanHeritageLayer, null),
                    React.createElement(NationalHeritageLayer, null),
                    React.createElement(WorldHeritagePropertiesLayer, null),
                    React.createElement(ArchaeologicalSitesLayer, null))),
                React.createElement(BuildingNumbersLayer, { revisionId: revisionId }),
                selectedBuildingId &&
                    React.createElement(BuildingHighlightLayer, { selectedBuildingId: selectedBuildingId, baseTileset: doesMapStyleColourBuildings(mapColourScale, categoryMapDefinitions)
                            ? mapColourScale
                            : 'date_year' })),
            React.createElement(Pane, { name: 'cc-label-overlay-pane', style: { zIndex: 1000 } },
                React.createElement(BoroughLabelLayer, null)),
            React.createElement(ZoomControl, { position: "topright" }),
            React.createElement(AttributionControl, { prefix: "" })),
        // Layer Coming soon must show on welcome (basic) and view/edit modes
        comingSoonTitle ? (React.createElement("div", { className: "map-legend coming-soon-legend" },
            React.createElement(Logo, { variant: "default" }),
            React.createElement("h4", { className: "h4" }, comingSoonTitle),
            React.createElement("p", { className: "data-intro" }, "Coming soon\u2026"))) : (mode !== 'basic' && (React.createElement(Legend, { mapColourScaleDefinitions: categoryMapDefinitions, mapColourScale: mapColourScale, onMapColourScale: onMapColourScale }))),
        React.createElement("div", { className: "switchers-of-layers-map-menu" },
            React.createElement(ThemeSwitcher, { onSubmit: darkLightThemeSwitch, currentTheme: darkLightTheme }),
            React.createElement(DataLayerSwitcher, null),
            (showLayerSelection == "enabled") ?
                React.createElement(React.Fragment, null,
                    React.createElement(SimpleLayerButton, { label: "Parcel Overlay", state: parcel, onClick: parcelSwitchOnClick }),
                    React.createElement(SimpleLayerButton, { label: "Governorates", state: governorates, onClick: governoratesSwitchOnClick }),
                    React.createElement(SimpleLayerButton, { label: "Protection Zones", state: conservation, onClick: conservationSwitchOnClick }),
                    React.createElement(SimpleLayerButton, { label: "MOH Zone", comingSoon: true, onClick: (e) => { e.preventDefault(); e.stopPropagation(); toggleComingSoon('MOH Zone'); } }),
                    React.createElement(SimpleLayerButton, { label: "Historic Aerial Photos", comingSoon: true, onClick: (e) => { e.preventDefault(); e.stopPropagation(); toggleComingSoon('Historic Aerial Photos'); } }),
                    React.createElement(SimpleLayerButton, { label: "Editable Building", state: editableBuildings, onClick: editableBuildingsSwitchOnClick }),
                    React.createElement(SimpleLayerButton, { label: "OpenStreetMap", state: borough, onClick: boroughSwitchOnClick }))
                : React.createElement(React.Fragment, null)),
        React.createElement(SearchBox, { onLocate: handleLocate })));
};
function ClickHandler({ onClick }) {
    useMapEvent('click', (e) => onClick(e));
    return null;
}
function MapBackgroundColor({ theme }) {
    const map = useMap();
    useEffect(() => {
        map.getContainer().style.backgroundColor = mapBackgroundColor[theme];
    });
    return null;
}
function MapViewport({ position, zoom }) {
    const map = useMap();
    useEffect(() => {
        map.setView(position, zoom);
    }, [position, zoom]);
    return null;
}
